import { evalCondition, storeGet } from 'mailup-react-helpers'
import {
  STORE_KEY_ID_LIST,
  STORE_KEY_LOGIN_TYPE
} from 'mailup-react-helpers'

import {
  beMessApiGetEntitiesThatUseMessage,
  beMessGetMessageDetails
} from 'Api/BeMessApi'

import { beEmailCheckupFastMessageCheck } from 'Api/beEmailCheckupApi'
import { beSendGoCheckSendingProgress } from 'Api/beSendGoApi'

import {
  ENTITY_TYPE_AUTOMATION_WORKFLOW,
  ENTITY_TYPE_TRIGGER,
  LOGIN_TYPE_WEBLOGIN
} from 'Constants'
import { beAsyncTrackingApiCheckTrackingDomainIsCustomizable } from 'Api/beAsyncTracking'
import {
  CUSTOM_DOMAIN_CUSTOMIZABLE,
  CUSTOM_DOMAIN_NOT_CUSTOMIZABLE
} from 'Constants/index'
import {
  beAccountSettingsAddSilencedAlerts,
  beAccountSettingsGetSilencedAlerts,
  beAccountSettingsGetUserSetting,
  beAccountSettingsUpsertUserSettings
} from 'Api/beAccountSettingsApi'

export async function getMessageDetails(idMessage) {
  const {
    IdMessage: id,
    MessageType: messageType,
    IsBee: isBee,
    Subject: subject
  } = await beMessGetMessageDetails(idMessage)

  return { id, messageType, isBee, subject }
}

export async function checkMessage(idMessage) {
  const checks = {
    Checks: [
      'Size',
      'MaxSize',
      'Attachments',
      'DynamicContents',
      'LinksWoTracking',
      'UnsubscribeLink',
      'Fields',
      'Preheader',
      'WebAnalytics',
      'LiquidCode',
      'Links',
      'Images',
      'Content'
    ]
  }

  const {
    CheckLiquidCode,
    CheckAttachments,
    CheckDynamicContents,
    CheckSize,
    CheckBlacklistResult,
    CheckLinksWoTracking,
    CheckUnsubscribeLink,
    CheckFields,
    CheckMaxSize,
    CheckPreheader,
    CheckWebAnalytics,
    CheckContent
  } = await beEmailCheckupFastMessageCheck(idMessage, checks)

  let domainCustomizableStatus = CUSTOM_DOMAIN_NOT_CUSTOMIZABLE
  try {
    const resultDomainIsCustomizable = await beAsyncTrackingApiCheckTrackingDomainIsCustomizable()
    const domainIsCustomizableOermission = evalCondition(
      'uo:GEST AND uo:SETTINGS'
    )
    if (resultDomainIsCustomizable && domainIsCustomizableOermission) {
      domainCustomizableStatus = resultDomainIsCustomizable.IsCustomizable
    }
  } catch (error) {
    domainCustomizableStatus = CUSTOM_DOMAIN_NOT_CUSTOMIZABLE
  }

  const entities = await beMessApiGetEntitiesThatUseMessage(idMessage)

  const workflows = entities.filter(
    ({ Type }) =>
      Type === ENTITY_TYPE_TRIGGER || Type === ENTITY_TYPE_AUTOMATION_WORKFLOW
  )

  const wrongDynamicTagsSubject = CheckFields.WrongDynamicTags.filter(
    ({ Location }) => Location === 'Subject'
  )
    .map(({ Value }) => Value.replace(/[[\]]/g, ''))
    .filter(f => f !== '')

  const wrongDynamicTagsPreheader = CheckFields.WrongDynamicTags.filter(
    ({ Location }) => Location === 'Preheader'
  )
    .map(({ Value }) => Value.replace(/[[\]]/g, ''))
    .filter(f => f !== '')

  const wrongDynamicTagsBody = CheckFields.WrongDynamicTags.filter(
    ({ Location }) => Location === 'Body'
  )
    .map(({ Value }) => Value.replace(/[[\]]/g, ''))
    .filter(f => f !== '')

  const blocking = [
    { key: 'LiquidCode', value: !CheckLiquidCode },
    { key: 'Attachments', value: !CheckAttachments.Check },
    { key: 'DynamicContents', value: !CheckDynamicContents.Check },
    { key: 'Size', value: !CheckSize.Check },
    { key: 'Links', value: CheckBlacklistResult.Action === 2 },
    { key: 'Content', value: CheckContent.Action === 1 }
  ]

  const blockingCount = blocking.filter(({ value }) => value).length

  const potential = [
    { key: 'LinksWoTracking', value: !CheckLinksWoTracking },
    { key: 'UnsubscribeLink', value: !CheckUnsubscribeLink },
    { key: 'MaxSize', value: !CheckMaxSize.Check },
    { key: 'Links', value: CheckBlacklistResult.Action === 1 },
    {
      key: 'Fields',
      value:
        wrongDynamicTagsSubject.length +
          wrongDynamicTagsPreheader.length +
          wrongDynamicTagsBody.length >
        0
    },
    { key: 'Workflows', value: workflows.length > 0 }
  ]

  const potentialCount = potential.filter(({ value }) => value).length

  const improvements = [
    { key: 'Preheader', value: !CheckPreheader },
    { key: 'WebAnalytics', value: !CheckWebAnalytics }
  ]
  if (domainCustomizableStatus !== CUSTOM_DOMAIN_NOT_CUSTOMIZABLE) {
    improvements.push({
      key: 'DomainCustomizable',
      value: domainCustomizableStatus === CUSTOM_DOMAIN_CUSTOMIZABLE
    })
  }
  const improvementsCount = improvements.filter(({ value }) => value).length

  const sectionDynamicFieldsDisabled =
    !CheckFields.IsDynamicFieldsEnabled && CheckFields.ContainsDynamicFields

  const blockingIssues = blocking.reduce(
    (acc, { key, value }) => ({ ...acc, [key]: value }),
    {}
  )

  const improvementsIssues = improvements.reduce(
    (acc, { key, value }) => ({ ...acc, [key]: value }),
    {}
  )

  return {
    blocking,
    blockingCount,
    blockingTotal: blocking.length,
    blockingIssues,
    potential,
    potentialCount,
    potentialTotal: sectionDynamicFieldsDisabled
      ? potential.length - 1
      : potential.length,
    improvements,
    improvementsCount,
    improvementsTotal: improvements.length,
    improvementsIssues,
    sectionDynamicFieldsDisabled,
    wrongDynamicTagsSubject,
    wrongDynamicTagsPreheader,
    wrongDynamicTagsBody,
    wrongAttachments: [
      ...(CheckAttachments.FileName ? CheckAttachments.FileName : [])
    ],
    wrongDynamicContents: [
      ...(CheckDynamicContents.ContentName
        ? CheckDynamicContents.ContentName
        : [])
    ],
    domainCustomizableStatus
  }
}

export async function isSendingMessage(idMessage) {
  const { Value } = await beSendGoCheckSendingProgress(idMessage)
  return Value
}

export async function modalCheck(idMessage) {
  const checks = {
    Checks: [
      'Size',
      'MaxSize',
      'Attachments',
      'DynamicContents',
      'LinksWoTracking',
      'UnsubscribeLink',
      'Fields',
      'Preheader',
      'WebAnalytics',
      'LiquidCode',
      'Links',
      'Content'
    ]
  }

  const checkObj = await beEmailCheckupFastMessageCheck(idMessage, checks)

  let domainCustomizableStatus = CUSTOM_DOMAIN_NOT_CUSTOMIZABLE
  try {
    const resultDomainIsCustomizable = await beAsyncTrackingApiCheckTrackingDomainIsCustomizable()
    const domainIsCustomizableOermission = evalCondition(
      'uo:GEST AND uo:SETTINGS'
    )
    if (resultDomainIsCustomizable && domainIsCustomizableOermission) {
      domainCustomizableStatus = resultDomainIsCustomizable.IsCustomizable
    }
  } catch (error) {
    domainCustomizableStatus = CUSTOM_DOMAIN_NOT_CUSTOMIZABLE
  }

  checkObj.CustomDomainCustomizable =
    domainCustomizableStatus === CUSTOM_DOMAIN_CUSTOMIZABLE

  return checkObj
}

export async function getEntitiesThatUseMessage(idMessage) {
  const entities = await beMessApiGetEntitiesThatUseMessage(idMessage)

  const workflows = entities.filter(
    ({ Type }) =>
      Type === ENTITY_TYPE_TRIGGER || Type === ENTITY_TYPE_AUTOMATION_WORKFLOW
  )
  return workflows.length > 0
}

export async function getSilencedAlerts() {
  const idList = 0 // parseInt(storeGet(STORE_KEY_ID_LIST))
  const result = await beAccountSettingsGetSilencedAlerts(idList)
  return result || {}
}

export async function getWhitelist(idMessage) {
  const idList = parseInt(storeGet(STORE_KEY_ID_LIST))
  const result = await beAccountSettingsGetUserSetting(
    idList,
    `whiteListDynamicField-NL-${encodeURIComponent(idMessage)}`
  )
  return result || {}
}

export async function getAllEntitiesThatUseMessage(idMessage) {
  const entities = await beMessApiGetEntitiesThatUseMessage(idMessage)
  return entities
}

export async function addSilencedAlerts(DataValue) {
  const idList = 0 // parseInt(storeGet(STORE_KEY_ID_LIST))
  const result = await beAccountSettingsAddSilencedAlerts(idList, DataValue)
  return result || {}
}

export async function updateWhitelist(idMessage, DataValue) {
  const idList = parseInt(storeGet(STORE_KEY_ID_LIST))
  const result = await beAccountSettingsUpsertUserSettings(
    idList,
    `whiteListDynamicField-NL-${encodeURIComponent(idMessage)}`,
    DataValue
  )
  return result || {}
}

export async function updateIssueVisibility(issueType, value, idMessage) {
  const loginType = storeGet(STORE_KEY_LOGIN_TYPE)

  if (loginType === LOGIN_TYPE_WEBLOGIN) {
    const curr = await beAccountSettingsGetUserSetting(
      0,
      'checkUpIssueVisibility'
    )
    const settings =
      curr && curr.DataValue && curr.DataValue !== ''
        ? JSON.parse(curr.DataValue)
        : []

    const newItem = {
      issueType,
      enable:
        value === 'enable' ? true : value === 'disable' ? false : undefined,
      disableForMessage: value === 'silent' ? [`${idMessage}`] : []
    }

    const newSettings = settings.find(o => o.issueType === issueType)
      ? settings.map(o => (o.issueType === issueType ? newItem : o))
      : settings.length > 0
      ? [...settings, newItem]
      : [newItem]

    await beAccountSettingsUpsertUserSettings(
      0,
      'checkUpIssueVisibility',
      newSettings
    )
  }
}
